import { Injectable, NgZone, inject, signal } from '@angular/core';
import { Observable, Subject, Subscription, fromEvent } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { ShortcutAction } from 'src/app/core/enums/shortcut-action.enum';
import { ShortcutItem } from 'src/app/core/interfaces/shortcut.interface';
import { ShortcutSettingsService } from 'src/app/modules/settings/services/shortcut-settings.service';
import { ShortcutActionRouterService } from 'src/app/core/services/shortcut-action-router.service';
import {
  buildShortcutLookupKeys,
  captureShortcutFromKeyboardEvent,
  toCompactShortcutKey,
} from 'src/app/core/utils/shortcut.util';

/**
 * Global keyboard shortcuts for the ERP.
 *
 * Call `loadShortcuts()` / `ensureShortcutsLoaded()` after authentication when HTTP includes the token.
 */
@Injectable({
  providedIn: 'root',
})
export class GlobalShortcutService {
  private readonly ngZone = inject(NgZone);
  private readonly shortcutSettingsService = inject(ShortcutSettingsService);
  private readonly shortcutActionRouter = inject(ShortcutActionRouterService);

  private documentKeydownSubscription: Subscription | null = null;
  private dispatchSubscription: Subscription | null = null;
  private shortcutsBootstrapInFlight = false;

  private readonly activeShortcutsWritable = signal<ShortcutItem[]>([]);
  /** Active shortcuts only (`isActive === true`, non-empty `keys`). */
  readonly activeShortcuts = this.activeShortcutsWritable.asReadonly();

  private readonly matchMap = new Map<string, ShortcutAction>();

  private readonly shortcutTriggeredSubject = new Subject<ShortcutAction>();
  readonly shortcutTriggered$ = this.shortcutTriggeredSubject.asObservable();

  constructor() {
    this.dispatchSubscription = this.shortcutTriggered$.subscribe((action) =>
      this.shortcutActionRouter.execute(action)
    );
    this.bindDocumentKeydownListener();
  }

  /**
   * Fetches shortcuts from the API and rebuilds the match map.
   */
  loadShortcuts(): Observable<void> {
    return this.shortcutSettingsService.getShortcuts().pipe(
      tap((items) => this.applyFullListFromServer(items)),
      map(() => undefined)
    );
  }

  /**
   * Loads shortcuts when the map is still empty (e.g. failed bootstrap or late token).
   */
  ensureShortcutsLoaded(): void {
    if (this.matchMap.size > 0 || this.shortcutsBootstrapInFlight) {
      return;
    }

    if (!sessionStorage.getItem('accessToken')) {
      return;
    }

    this.shortcutsBootstrapInFlight = true;
    this.loadShortcuts().subscribe({
      next: () => {
        this.shortcutsBootstrapInFlight = false;
      },
      error: () => {
        this.shortcutsBootstrapInFlight = false;
      },
    });
  }

  syncFromFullList(allItems: ShortcutItem[]): void {
    this.applyFullListFromServer(allItems);
  }

  mergeShortcutUpdate(shortcut: ShortcutItem): void {
    const without = this.activeShortcutsWritable().filter((item) => item.action !== shortcut.action);
    const next =
      shortcut.isActive && shortcut.keys?.trim()
        ? [...without, { ...shortcut }]
        : without;
    this.activeShortcutsWritable.set(next);
    this.rebuildMatchMap(next);
  }

  clearForSessionEnd(): void {
    this.activeShortcutsWritable.set([]);
    this.matchMap.clear();
    this.shortcutsBootstrapInFlight = false;
  }

  private applyFullListFromServer(allItems: ShortcutItem[]): void {
    const activeOnly = allItems.filter((item) => item.isActive === true && Boolean(item.keys?.trim()));
    this.activeShortcutsWritable.set(activeOnly);
    this.rebuildMatchMap(activeOnly);
  }

  private rebuildMatchMap(items: ShortcutItem[]): void {
    this.matchMap.clear();
    for (const item of items) {
      for (const lookupKey of buildShortcutLookupKeys(item.keys)) {
        this.matchMap.set(lookupKey, item.action);
      }
    }
  }

  private bindDocumentKeydownListener(): void {
    this.ngZone.runOutsideAngular(() => {
      const handler = (event: KeyboardEvent) => this.handleDocumentKeydown(event);
      this.documentKeydownSubscription = fromEvent<KeyboardEvent>(document, 'keydown', {
        capture: true,
      }).subscribe(handler);
    });
  }

  private handleDocumentKeydown(event: KeyboardEvent): void {
    if (this.matchMap.size === 0) {
      return;
    }

    const captured = captureShortcutFromKeyboardEvent(event);
    if (!captured?.normalized) {
      return;
    }

    const action = this.resolveAction(captured.normalized);
    if (action === undefined) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    this.ngZone.run(() => {
      this.shortcutTriggeredSubject.next(action);
    });
  }

  private resolveAction(normalizedShortcut: string): ShortcutAction | undefined {
    const direct = this.matchMap.get(normalizedShortcut);
    if (direct !== undefined) {
      return direct;
    }

    const compact = toCompactShortcutKey(normalizedShortcut);
    if (!compact) {
      return undefined;
    }

    return this.matchMap.get(compact);
  }
}
